import React from "react";
import MessageList from "./MessageList";
import EmptyState from "./EmptyState";
import EnvTenantSelector from "./EnvTenantSelector";
import ConfidentialAccessToggle from "./ConfidentialAccessToggle";

// Legacy chart-agent engines, slated for retirement once the AI Assistant reaches parity in
// production (Sprint 8a marks; a later sprint removes).
const LEGACY_ENGINES = ["ai", "ai2", "ai2-local"];

// Pure presentational pane. State lives in the page so the split view
// can drive two panes from one shared Composer.
export default function ChatPane({
  title,
  state,
  connected,
  onSelectExample,
  onClarificationSelect,
  // AI Assistant additions (Sprint 7). All optional — when showAssistantControls is false
  // (any non-ai-assistant engine, or prod uiMode) the header renders exactly as before.
  onFollowUpSelect,
  showAssistantControls = false,
  env,
  tenant,
  tenants,
  confidentialAccess = false,
  onEnvTenantChange,
  onConfidentialChange,
  // Chart Agent retirement (Sprint 8a). `engine` is the selected mode; `onEngineChange` lets
  // the deprecation banner switch to the new agent. Both optional for back-compat.
  engine,
  onEngineChange,
}) {
  const isEmpty = state.messages.length === 0;
  const isLegacy = LEGACY_ENGINES.includes(engine);
  const badge = deriveBadge({
    status: state.status,
    messageCount: state.messages.length,
    connected,
  });

  return (
    <div className="flex flex-col h-full min-w-0">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3 bg-white flex-shrink-0 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {showAssistantControls && (
          <div className="flex items-center gap-4 flex-wrap">
            <EnvTenantSelector
              env={env}
              tenant={tenant}
              tenants={tenants}
              onChange={onEnvTenantChange}
            />
            <ConfidentialAccessToggle
              confidentialAccess={confidentialAccess}
              onChange={onConfidentialChange}
            />
          </div>
        )}
        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded ${badge.toneClass}`}>
          {badge.label}
        </span>
      </div>
      {isLegacy && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-xs text-yellow-800 flex-shrink-0">
          This is a legacy chart agent.{" "}
          {typeof onEngineChange === "function" ? (
            <>
              The new{" "}
              <button
                type="button"
                onClick={() => onEngineChange("ai-assistant")}
                className="font-medium underline hover:text-yellow-900"
              >
                AI Assistant
              </button>{" "}
              answers a broader range of questions with the same chart capabilities. Try it.
            </>
          ) : (
            <>The new AI Assistant answers a broader range of questions with the same chart capabilities.</>
          )}
        </div>
      )}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isEmpty ? (
          <EmptyState onSelectExample={onSelectExample} />
        ) : (
          <MessageList
            messages={state.messages}
            status={state.status}
            error={state.lastError}
            onClarificationSelect={onClarificationSelect}
            onFollowUpSelect={onFollowUpSelect}
          />
        )}
      </div>
    </div>
  );
}

function deriveBadge({ status, messageCount, connected }) {
  if (!connected) return { label: "Reconnecting…", toneClass: "bg-amber-100 text-amber-700" };
  if (status === "error") return { label: "Error", toneClass: "bg-red-100 text-red-700" };
  if (status === "in_flight") return { label: "Thinking…", toneClass: "bg-blue-100 text-blue-700" };
  if (status === "awaiting_clarification") return { label: "Needs input", toneClass: "bg-blue-100 text-blue-700" };
  if (messageCount === 0) return { label: "Idle", toneClass: "bg-gray-100 text-gray-600" };
  return { label: "Done", toneClass: "bg-emerald-100 text-emerald-700" };
}
